import React from "react";
import { motion } from "framer-motion";
import {
  Briefcase,
  UserMinus,
  BadgeCheck,
  TrendingUp,
  PercentDiamond,
  Clock,
} from "lucide-react";

const contentCards = [
  {
    id: "managed",
    icon: Briefcase,
    accent: "#FF4500",
    eyebrow: "Fully managed, end-to-end",
    title: "We own the engine. You own the close.",
    body: "One performance-based outbound system replacing your SDR, ads and lead-gen stack. ICP, infra, outreach, qualification and booking; all run by our team.",
  },
  {
    id: "zero-sdr",
    icon: UserMinus,
    accent: "#A7F3D0",
    eyebrow: "Zero SDR headcount",
    title: "No hiring. No turnover.",
    body: "Forget ramps, churn and management overhead. The team is ours.",
  },
  {
    id: "pre-qual",
    icon: BadgeCheck,
    accent: "#FFDAB9",
    eyebrow: "Pre-qualified, not raw",
    title: "Only high-intent reaches you.",
    body: "Every meeting comes with BANT summary and pre-call notes attached.",
  },
];

const statCards = [
  {
    id: "conv-15",
    icon: PercentDiamond,
    accent: "#A7F3D0",
    eyebrow: "Conversion",
    stat: "+15%",
    title: "Higher close rate",
    body: "Booked meetings convert ~15% higher than your previous pipeline averages.",
  },
  {
    id: "pipeline-10x",
    icon: TrendingUp,
    accent: "#FF4500",
    eyebrow: "Pipeline",
    stat: "10×",
    title: "Stronger pipeline",
    body: "Targeting & qualification rebuilt at the core. Not bolted on top of generic tools.",
  },
  {
    id: "hours-50",
    icon: Clock,
    accent: "#FFDAB9",
    eyebrow: "Time back",
    stat: "50+",
    title: "Hours saved / week",
    body: "Across your revenue team, every single week.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

function ContentCard({ c, i, big = false }) {
  return (
    <motion.article
      custom={i}
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
      data-testid={`bento-${c.id}`}
      className={`group relative overflow-hidden rounded-3xl border border-[#2A2E3D] bg-[#151821] p-8 md:p-10 hover:border-[#FF4500]/50 transition-all duration-500 hover:-translate-y-1 flex flex-col ${
        big ? "md:row-span-2" : ""
      }`}
    >
      <div
        className="absolute -inset-1 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          background: `radial-gradient(600px circle at 30% 0%, ${c.accent}20, transparent 60%)`,
        }}
      />
      <div className="relative flex items-start justify-between">
        <span
          className="text-[10px] uppercase tracking-[0.3em]"
          style={{ color: c.accent }}
        >
          {c.eyebrow}
        </span>
        <c.icon className="w-6 h-6 text-[#9CA3AF]" strokeWidth={1.3} />
      </div>
      <div className={`relative ${big ? "mt-10" : "mt-8"}`}>
        <h3
          className={`font-editorial tracking-tight text-[#EDEDED] ${
            big ? "text-4xl sm:text-5xl lg:text-6xl" : "text-2xl sm:text-3xl"
          }`}
        >
          {c.title}
        </h3>
        <p
          className={`mt-4 text-[#9CA3AF] leading-relaxed text-base ${
            big ? "max-w-lg" : ""
          }`}
        >
          {c.body}
        </p>
      </div>
      {big ? (
        <div className="relative mt-auto pt-10">
          <div className="flex flex-wrap gap-2">
            {["ICP", "Infra", "Outreach", "Qualification", "Booking"].map(
              (chip) => (
                <span
                  key={chip}
                  className="px-3 py-1.5 rounded-full border border-[#2A2E3D] text-xs uppercase tracking-[0.2em] text-[#9CA3AF] hover:border-[#FF4500]/50 hover:text-[#EDEDED] transition-colors"
                >
                  {chip}
                </span>
              )
            )}
          </div>
        </div>
      ) : null}
    </motion.article>
  );
}

function StatCard({ c, i }) {
  return (
    <motion.article
      custom={i}
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
      data-testid={`bento-${c.id}`}
      className="group relative overflow-hidden rounded-3xl border border-[#2A2E3D] bg-[#0F1117] p-8 md:p-10 hover:border-[#FF4500]/50 transition-all duration-500 hover:-translate-y-1 flex flex-col min-h-[360px]"
    >
      <div
        className="absolute -inset-1 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          background: `radial-gradient(600px circle at 30% 0%, ${c.accent}20, transparent 60%)`,
        }}
      />
      <div className="relative flex items-start justify-between">
        <span
          className="text-[10px] uppercase tracking-[0.3em]"
          style={{ color: c.accent }}
        >
          {c.eyebrow}
        </span>
        <c.icon className="w-6 h-6 text-[#9CA3AF]" strokeWidth={1.3} />
      </div>
      <div className="relative my-4">
        <div
          className="tick-num text-[5.5rem] sm:text-[7rem] leading-[0.85]"
          style={{ color: c.accent }}
        >
          {c.stat}
        </div>
      </div>
      <div className="relative mt-auto">
        <h3 className="font-editorial tracking-tight text-2xl sm:text-3xl text-[#EDEDED]">
          {c.title}
        </h3>
        <p className="mt-4 text-[#9CA3AF] text-base leading-relaxed">
          {c.body}
        </p>
      </div>
    </motion.article>
  );
}

export default function Differentiators() {
  return (
    <section
      id="differentiators"
      data-testid="differentiators-section"
      className="relative py-24 md:py-36 bg-[#0B0D12]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="mb-16 max-w-3xl">
          <p className="text-xs uppercase tracking-[0.3em] text-[#FF4500] mb-4">
            Why prospectivly
          </p>
          <h2 className="font-editorial font-light text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter">
            A pipeline engine,
            <br />
            <span className="italic">not another tool</span> in your stack.
          </h2>
        </div>

        {/* Row 1: Content cards (1 big left + 2 small stacked right, 50/50 split) */}
        <div className="grid grid-cols-1 md:grid-cols-2 md:auto-rows-fr gap-5 md:gap-6 mb-5 md:mb-6">
          {contentCards.map((c, i) => (
            <ContentCard key={c.id} c={c} i={i} big={i === 0} />
          ))}
        </div>

        {/* Row 2: Stat cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {statCards.map((c, i) => (
            <StatCard key={c.id} c={c} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
